/**
 * <compare-products>
 * Loads candidate columns for the "Compare Similar Products" table and merges
 * them into the CSS grid rendered by sections/compare-products.liquid.
 *
 * Sources (in order):
 *  - Shopify Product Recommendations API (Section Rendering API, same section id)
 *  - <template data-compare-fallback> rendered server-side from the primary collection
 *
 * Both sources emit identical column markup (snippets/compare-products-column.liquid),
 * so this element only has to dedupe, cap, prune empty rows and stripe the rows.
 */
if (!customElements.get('compare-products')) {
  customElements.define(
    'compare-products',
    class CompareProducts extends HTMLElement {
      constructor() {
        super();
        this.grid = this.querySelector('.compare-products__grid');
        this.max = parseInt(this.dataset.max, 10) || 4;
        this.source = this.dataset.source || 'auto';
        this.currentId = this.dataset.productId;
        this.designMode = this.dataset.designMode === 'true';
        this.started = false;
      }

      connectedCallback() {
        if (this.started || !this.grid) return;
        this.started = true;

        if (this.dataset.ready === 'true') {
          // Manual override: columns were rendered server-side.
          this.finalize();
          return;
        }

        const start = () => this.load();
        if ('requestIdleCallback' in window) {
          requestIdleCallback(start, { timeout: 1500 });
        } else if (window.FoxTheme && FoxTheme.Motion && FoxTheme.Motion.inView) {
          FoxTheme.Motion.inView(this, start, { margin: '0px 0px 400px 0px' });
        } else {
          start();
        }
      }

      async load() {
        let candidates = [];

        if (this.source !== 'collection' && this.dataset.url) {
          try {
            const response = await fetch(this.dataset.url);
            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            candidates = Array.from(doc.querySelectorAll('[data-compare-columns] [data-compare-col]'));
          } catch (error) {
            console.error('Compare products: recommendations request failed', error);
          }
        }

        if (this.source !== 'recommendations') {
          const fallback = this.querySelector('template[data-compare-fallback]');
          if (fallback) {
            candidates = candidates.concat(Array.from(fallback.content.querySelectorAll('[data-compare-col]')));
          }
        }

        const seen = new Set([this.currentId]);
        this.grid.querySelectorAll('[data-compare-col]').forEach((col) => seen.add(col.dataset.productId));

        const picked = [];
        for (const col of candidates) {
          const id = col.dataset.productId;
          if (!id || seen.has(id)) continue;
          seen.add(id);
          picked.push(col);
          if (picked.length >= this.max) break;
        }

        if (picked.length) {
          // Insert as HTML in one go so quick-add buttons exist before their
          // <quick-view-modal> connects and binds its aria-controls triggers.
          this.grid.insertAdjacentHTML('beforeend', picked.map((col) => col.outerHTML).join(''));
        }

        this.finalize();
      }

      finalize() {
        const similarCount = this.grid.querySelectorAll('[data-compare-col]:not([data-current])').length;

        if (similarCount === 0) {
          if (this.designMode) {
            const notice = this.querySelector('.compare-products__notice');
            if (notice) notice.hidden = false;
            this.classList.add('is-loaded', 'is-empty');
            this.grid.closest('.compare-products__scroller')?.setAttribute('hidden', '');
          } else {
            const section = this.closest('.shopify-section');
            if (section) section.remove();
            else this.remove();
          }
          this.dispatchEvent(new CustomEvent('compare-products:empty', { bubbles: true }));
          return;
        }

        this.pruneEmptyRows();
        this.stripeRows();
        this.classList.add('is-loaded');
        this.dispatchEvent(new CustomEvent('compare-products:loaded', { bubbles: true, detail: { count: similarCount } }));
      }

      /** Remove rows where none of the displayed products has a value (kept in the editor). */
      pruneEmptyRows() {
        if (this.designMode) return;
        const labels = this.grid.querySelectorAll('.compare-products__col--labels [data-row]');
        labels.forEach((label) => {
          const key = label.dataset.row;
          if (key === 'head' || key === 'action') return;
          const cells = this.grid.querySelectorAll(`[data-compare-col] [data-row="${key}"]`);
          const allEmpty = Array.from(cells).every((cell) => cell.hasAttribute('data-empty'));
          if (allEmpty) {
            label.remove();
            cells.forEach((cell) => cell.remove());
          }
        });
      }

      /** Recompute grid row count and alternate row shading. */
      stripeRows() {
        const labels = Array.from(this.grid.querySelectorAll('.compare-products__col--labels [data-row]'));
        this.grid.style.setProperty('--compare-rows', labels.length);
        this.grid.style.setProperty('--compare-cols', this.grid.querySelectorAll('[data-compare-col]').length);

        labels.forEach((label, index) => {
          const key = label.dataset.row;
          // Head row is index 0; shade every other value row starting with the first one.
          const shaded = key !== 'head' && key !== 'action' && index % 2 === 1;
          this.grid.querySelectorAll(`[data-row="${key}"]`).forEach((cell) => cell.classList.toggle('is-odd', shaded));
        });
      }
    }
  );
}
